import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { PRIORITIES, PRIORITY_LABELS } from "../lib/tickets";
import { LoadError, SaveBar, SectionHeader } from "./Settings";
import { PriorityBadge } from "../components/tickets/TicketBits";
import { Input } from "../components/ui/input";

const FIELDS = [["first_response_hours", "Première réponse", 720], ["resolution_hours", "Résolution", 2160]];
const keyOf = (priority, field) => `sla.${priority}.${field}`;

// The server's rule (settings.models.check_section): a whole number of hours within bounds, and the answer before the resolution.
export function slaErrors(values) {
  const errors = {};
  for (const p of PRIORITIES) {
    for (const [field, , max] of FIELDS) {
      const n = Number(values[keyOf(p, field)]);
      if (!Number.isInteger(n) || n < 1 || n > max) errors[keyOf(p, field)] = `Entre 1 et ${max} heures`;
    }
    const first = Number(values[keyOf(p, "first_response_hours")]);
    const resolution = Number(values[keyOf(p, "resolution_hours")]);
    if (!errors[keyOf(p, "resolution_hours")] && !errors[keyOf(p, "first_response_hours")] && first > resolution) {
      errors[keyOf(p, "resolution_hours")] = "Pas avant la première réponse";
    }
  }
  return errors;
}

// Paramètres › Support — response and resolution objectives per priority (Ordre 08, D-16). settings:view to read, settings:manage
// to change; each ticket keeps the objectives of its creation, so a change never rewrites the past.
export default function SettingsTickets() {
  const { user } = useAuth();
  const canManage = hasPermission(user, "settings:manage");
  const [saved, setSaved] = useState(null);
  const [values, setValues] = useState({});
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  const apply = (settings) => {
    setSaved(settings);
    setValues(Object.fromEntries(Object.entries(settings).map(([k, v]) => [k, String(v)])));
  };
  const load = async () => {
    setLoadFailed(false);
    try {
      const { data } = await api.get("/settings/tickets");
      apply(data.settings);
    } catch (e) {
      setLoadFailed(true);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const errors = slaErrors(values);
  const dirty = saved != null && Object.keys(saved).some((k) => String(saved[k]) !== values[k]);
  const save = async () => {
    setSaving(true);
    try {
      const changed = Object.fromEntries(Object.keys(saved).filter((k) => String(saved[k]) !== values[k]).map((k) => [k, Number(values[k])]));
      const { data } = await api.patch("/settings/tickets", changed);
      apply(data.settings);
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (loadFailed) return <LoadError onRetry={load} />;
  if (saved == null) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-jamora" /></div>;

  return (
    <div className="space-y-4" data-testid="settings-tickets">
      <SectionHeader title="Support" description="Délais de première réponse et de résolution des tickets, selon leur priorité." />
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <div className="hidden sm:grid grid-cols-[8rem_1fr_1fr] gap-3 text-xs font-semibold text-slate-500">
          <span>Priorité</span>{FIELDS.map(([, label]) => <span key={label}>{label}</span>)}
        </div>
        {PRIORITIES.map((p) => (
          <div key={p} className="grid grid-cols-2 sm:grid-cols-[8rem_1fr_1fr] gap-3 items-start border-t border-slate-100 pt-3 first-of-type:border-0">
            <div className="col-span-2 sm:col-span-1 pt-2"><PriorityBadge priority={p} /></div>
            {FIELDS.map(([field, label]) => {
              const id = `sla-${p}-${field}`;
              const error = errors[keyOf(p, field)];
              return (
                <div key={field} className="space-y-1">
                  <label htmlFor={id} className="text-xs text-slate-500 sm:sr-only">{label} ({PRIORITY_LABELS[p]})</label>
                  <div className="flex items-center gap-2">
                    <Input id={id} type="number" min={1} value={values[keyOf(p, field)] ?? ""} disabled={!canManage} className="w-24"
                      onChange={(e) => setValues((v) => ({ ...v, [keyOf(p, field)]: e.target.value }))} aria-invalid={!!error} data-testid={id} />
                    <span className="text-sm text-slate-500">heures</span>
                  </div>
                  {error && <p className="text-xs text-red-600">{error}</p>}
                </div>
              );
            })}
          </div>
        ))}
        <ul className="text-xs text-slate-500 space-y-1 list-disc pl-4 border-t border-slate-100 pt-3">
          <li>Heures calendaires, 24 h sur 24. Le temps passé en attente de la pharmacie ou d'un tiers ne compte pas dans la résolution.</li>
          <li>La première réponse est le premier échange avec la pharmacie consigné sur le ticket.</li>
          <li>Un changement ne vaut que pour les tickets ouverts ensuite : chaque ticket garde les délais de sa création.</li>
        </ul>
      </div>
      <SaveBar dirty={dirty} saveDisabled={Object.keys(errors).length > 0} saving={saving} canManage={canManage} onSave={save} onRevert={() => apply(saved)} />
    </div>
  );
}
